<template>
  <form v-if="value" @submit.prevent="onSubmit">
    <modal
      v-if="combinationsManagerVisible"
      title="Gestion des combinaisons"
      @close="hideCombinationsManager"
      size="lg"
    >
      <combinations />
    </modal>

    <form-sticky-header
      :title="title"
      :links="{
        'form-general': 'Général',
        'form-standards': 'Normes et sécurité',
        /*'form-preview': 'Aperçu',*/ 'form-properties': 'Propriétés',
        'form-images': 'Images des déclinaisons',
      }"
    >
      <slot name="actions"></slot>
    </form-sticky-header>

    <form-fieldset title="Général" id="form-general">
      <template slot="description">
        <p class="text-muted">
          Les produits regroupent des déclinaisons, c'est-à-dire une version
          (couleur, taille&hellip;) d'un produit. Configurez ici le produit de
          manière générale, puis rendez-vous sur la gestion des déclinaisons
          pour renseigner les détails de chaque référence.<br />
          <br />
          N'oubliez pas de <strong>publier votre produit</strong> si celle-ci
          n'apparaît pas dans l'interface.
        </p>
        <hr />
        <form-group>
          <form-switch v-model="model.published"
            >Publier ce produit</form-switch
          >
        </form-group>
      </template>

      <row>
        <column sm="6">
          <form-group label="Nom du produit" required>
            <form-input
              type="text"
              v-model.trim="model.name"
              placeholder="Ex : Gilet"
              required
            />
            <span slot="help"
              >Le nom du produit tel qu'il apparaît dans l'interface.</span
            >
          </form-group>
        </column>
        <column sm="6">
          <form-group label="Type" required>
            <form-select
              v-model="model.kind"
              :options="ProductKinds"
              @change="onChangeKind"
              placeholder="Choisir un type..."
              required
            />
            <span slot="help"
              >Une sélection de propriétés sera automatiquement complétée.</span
            >
          </form-group>
        </column>
      </row>

      <row>
        <column sm="6">
          <form-group label="Ordre d'affichage">
            <form-order v-model.numeric="model.order" />
          </form-group>
        </column>
        <column sm="6">
          <form-group label="Collection">
            <form-select
              v-model="model.collection"
              :options="collections"
              placeholder="Choisir la collection"
            />
            <span slot="help"
              >Rendez-vous sur la partie
              <router-link :to="{ name: 'collections' }"
                >collections</router-link
              >
              pour gérer cette liste.</span
            >
          </form-group>
        </column>
      </row>

      <form-group label="Combinaison / Groupe">
        <span slot="help"
          >Vous pouvez organiser les produits dans des combinaisons ou des
          groupes afin de modifier l'affichage dans le configurateur.</span
        >
        <row>
          <column sm="6">
            <form-checkbox v-model="modelHasCombination"
              >Ce produit fait partie d'une combinaison / d'un
              groupe</form-checkbox
            >
          </column>
          <column sm="6" v-if="modelHasCombination">
            <row>
              <column sm="8">
                <form-select
                  v-model="modelCombination"
                  :options="combinations"
                  placeholder="Choisir une combinaison / un groupe"
                  required
                />
              </column>
              <column sm="4">
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="openCombinationsManager"
                >
                  Gérer
                </button>
              </column>
            </row>
          </column>
        </row>
      </form-group>
    </form-fieldset>

    <!--<form-fieldset title="Aperçu" id="form-preview">-->
    <!--<template slot="description">-->
    <!--<p class="text-muted">-->
    <!--Aperçu du produit-->
    <!--</p>-->
    <!--</template>-->

    <!--<form-group label="Image de prévisualisation">-->
    <!--<form-file-upload v-model="model.preview" image />-->
    <!--</form-group>-->
    <!--</form-fieldset>-->

    <form-fieldset title="Normes et sécurité" id="form-standards">
      <template slot="description">
        <p class="text-muted">
          Cette section permet de renseigner les informations liées aux normes
          de sécurité et aux risques.
        </p>
      </template>

      <row>
        <column sm="6">
          <form-group label="Normes du produit">
            <form-multiselect
              v-model="model.standards"
              :options="ProductStandards"
              multiple
            />
          </form-group>
        </column>
      </row>
      <div class="Standards">
        <button
          v-for="(standardInfos, standard) in ProductStandardsInfos"
          type="button"
          class="btn btn-default btn-icon text-grey-300"
          :class="{
            'btn-info text-white':
              (model.standards || []).indexOf(standard) !== -1,
          }"
          @click="toggleStandard(standard)"
          :title="standardInfos.name"
          v-html="standardInfos.svg"
        />
      </div>
    </form-fieldset>

    <form-fieldset title="Propriétés" id="form-properties">
      <template slot="description">
        <p class="text-muted">
          Les propriétés du produit permettent de créer des déclinaisons et de
          proposer différentes versions du produit dans le configurateur.<br />
          <br />
          Selon le type de produit, une sélection est proposée&nbsp;: cependant
          il faut adapter les propriétés à chaque produit, en enlever ou
          rajouter certaines, et surtout renseigner les options possibles. Par
          exemple, pour une couleur on choisira les différentes options
          possibles dans la
          <router-link :to="{ name: 'colors' }">liste des couleurs</router-link
          >&nbsp;; pour un choix prédéfini, on proposera les options (ex :
          &laquo;&nbsp;Cuir&nbsp;&raquo;,
          &laquo;&nbsp;Synthétique&nbsp;&raquo;...)&nbsp;; pour les vêtements,
          on pourra choisir le sexe&nbsp;; enfin, le texte libre permet de
          renseigner des informations supplémentaires sur une déclinaison.
        </p>
      </template>

      <div
        v-for="(prop, propIndex) in model.properties"
        :key="prop.id || propIndex"
      >
        <row>
          <column lg="2" sm="6">
            <form-group label="Propriété" required>
              <form-input
                type="text"
                v-model.trim="model.properties[propIndex].name"
                placeholder="Ex : Taille"
                required
              />
            </form-group>
          </column>
          <column lg="2" sm="6">
            <form-group label="Type" required>
              <form-select
                v-model="model.properties[propIndex].kind"
                :class="{ 'bg-warning': isDuplicateSexProperty(prop) }"
                :options="ProductPropertyKinds"
                required
              />
            </form-group>
          </column>
          <column lg="5" sm="12">
            <div v-show="model.properties[propIndex].kind === 'text'">
              <form-group label=" ">
                <p class="no-margin text-muted mt-10">
                  Texte libre renseigné pour chaque déclinaison
                </p>
              </form-group>
            </div>
            <div v-show="model.properties[propIndex].kind === 'color'">
              <form-group label="Couleurs disponibles" required>
                <form-select-color
                  v-model="model.properties[propIndex].colors"
                  :colors="colors"
                  multiple
                />
              </form-group>
            </div>
            <div v-show="model.properties[propIndex].kind === 'tag'">
              <form-group label="Options possibles" required>
                <form-tags v-model="model.properties[propIndex].tags" />
              </form-group>
            </div>
            <div v-show="model.properties[propIndex].kind === 'sex'">
              <form-group label=" ">
                <p
                  v-if="!isDuplicateSexProperty(prop)"
                  class="no-margin text-muted mt-10"
                >
                  Homme, Femme, Unisexe
                </p>
                <p v-else class="no-margin text-warning text-semibold mt-10">
                  Il ne peut y avoir qu'une seule propriété Sexe
                </p>
              </form-group>
            </div>
          </column>
          <column lg="2" sm="8">
            <form-group label=" ">
              <form-checkbox v-model="model.properties[propIndex].required"
                >Obligatoire</form-checkbox
              >
            </form-group>
          </column>
          <column lg="1" sm="4">
            <form-group label=" " class="text-right">
              <button
                type="button"
                @click="removeProperty(propIndex)"
                class="btn btn-danger"
              >
                <i class="icon-bin"></i>
              </button>
            </form-group>
          </column>
        </row>
        <hr />
      </div>

      <button type="button" @click="addProperty" class="btn btn-primary">
        <i class="icon-add"></i> Ajouter une propriété
      </button>
    </form-fieldset>

    <form-fieldset title="Images des déclinaisons" id="form-images">
      <template slot="description">
        <p class="text-muted">
          Téléchargez en une seule fois toutes les images des déclinaisons du
          produit afin de pouvoir plus rapidement les utiliser par la suite.
        </p>
      </template>

      <form-group label="">
        <form-checkbox v-model="model.fullImage"
          >Afficher en taille maximum (sans marges)</form-checkbox
        >
      </form-group>
      <form-file-upload v-model="model.images" image multiple editable />
    </form-fieldset>
  </form>
</template>

<style>
.Standards {
  margin: 0 -4px;
  padding: 0;
}
.Standards button {
  box-shadow: 0 0 0 rgba(0, 0, 0, 0.5);
  margin: 3px;
  position: relative;
  transform: scale(1);
  transform-origin: center center;
  transition: transform 0.2s ease;
  z-index: 1;
  vertical-align: top;
}
.Standards svg {
  fill: currentColor;
  transition: width 0.2s ease;
  width: 60px;
}
.Standards button:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  transform: scale(1.4);
  z-index: 10;
}
</style>

<script>
import _get from "lodash/get";
import _omit from "lodash/omit";
import _map from "lodash/map";
import _xor from "lodash/xor";

import {
  ProductKindsArray as ProductKinds,
  ProductPropertyKindsArray as ProductPropertyKinds,
  ProductDefaultProperties,
  ProductStandardsArray as ProductStandards,
  ProductStandardsInfos,
} from "../../common/types";

import ColorsListQuery from "../colors/graphql/ColorsListQuery.gql";
import CollectionListQuery from "../collections/graphql/CollectionListQuery.gql";
import CombinationListQuery from "../combinations/graphql/CombinationListQuery.gql";

export default {
  components: {
    FormStickyHeader: () => import("../../components/FormStickyHeader"),
    FormFileUpload: () => import("../../components/FormFileUpload"),
    FormOrder: () => import("../../components/FormOrder"),
    FormSelectColor: () => import("../../components/FormSelectColor"),
    FormSwitch: () =>
      import("package-common-vuejs/src/components/form/FormSwitch"),
    FormTags: () => import("package-common-vuejs/src/components/form/FormTags"),
    Combinations: () => import("../combinations/Combinations"),
    Modal: () => import("package-common-vuejs/src/components/Modal"),
    FormMultiselect: () =>
      import("package-common-vuejs/src/components/form/FormMultiselect"),
  },
  props: {
    title: String,
    value: Object,
  },
  apollo: {
    colors: {
      query: ColorsListQuery,
      fetchPolicy: "network-only",
      variables() {
        return {
          pagination: {
            limit: 999,
          },
        };
      },
      update: (data) => {
        return _map(data.results.edges, "node");
      },
    },
    collections: {
      query: CollectionListQuery,
      fetchPolicy: "network-only",
      variables() {
        return {
          pagination: {
            limit: 999,
          },
        };
      },
      update: (data) =>
        _map(data.results.edges, ({ node: { id, name } }) => ({
          value: id,
          label: name,
        })),
    },
    combinations: {
      query: CombinationListQuery,
      fetchPolicy: "network-only",
      variables() {
        return {
          pagination: {
            limit: 999,
          },
        };
      },
      update: (data) =>
        _map(data.results.edges, ({ node: { id, name, parts } }) => ({
          value: id,
          label: name,
          children: (parts || []).map(({ id, name }) => ({
            value: id,
            label: name,
          })),
        })),
    },
  },
  data() {
    return {
      model: {},
      collections: [],
      combinations: [],
      colors: [],

      modelHasCombination: false,

      combinationsManagerVisible: false,

      ProductKinds,
      ProductPropertyKinds,
      ProductStandards,
      ProductStandardsInfos,
    };
  },
  computed: {
    modelCombination: {
      get() {
        return this.model.combinationPart;
      },
      set(value) {
        if (!value) {
          this.model.combination = null;
          this.model.combinationPart = null;
          return;
        }
        const combination = (this.combinations || []).find(
          ({ children }) =>
            !!children.find(({ value: childValue }) => childValue === value)
        );
        this.model.combination = combination.value;
        this.model.combinationPart = value;
      },
    },
  },
  created() {
    this.updateModel(this.value);
    if (this.value && !this.value.id) {
      // On creation
      this.onChangeKind(this.value.kind);
    }
  },
  watch: {
    value() {
      this.updateModel(this.value);
    },
  },
  methods: {
    openCombinationsManager() {
      this.combinationsManagerVisible = true;
    },
    hideCombinationsManager() {
      this.$apollo.queries.combinations.refetch();
      this.combinationsManagerVisible = false;
    },
    onChangeKind(kind) {
      this.model.properties = ProductDefaultProperties[kind] || [];
    },
    isDuplicateSexProperty({ kind }) {
      return (
        kind === "sex" &&
        (this.model.properties || []).filter(({ kind }) => kind === "sex")
          .length > 1
      );
    },
    addProperty() {
      this.$set(this.model, "properties", [
        ...(this.model.properties || []),
        {
          name: `Propriété ${(this.model.properties || []).length + 1}`,
          kind: "tag",
        },
      ]);
    },
    removeProperty(propIndex) {
      this.$set(this.model, "properties", [
        ...this.model.properties.slice(0, propIndex),
        ...this.model.properties.slice(propIndex + 1),
      ]);
    },
    updateModel(value) {
      this.model = {
        ...value,
        preview: _get(value, "preview.id"),
        collection: _get(value, "collection.id"),
        combination: _get(value, "combination.id"),
        combinationPart: _get(value, "combinationPart.id"),
        properties: ((value && value.properties) || [])
          .filter(({ name }) => !!name)
          .map(({ colors, ...props }) => ({
            ...props,
            colors: _map(colors || [], "id"),
          })),
        images: ((value && value.images) || []).map(({ id }) => id),
      };
      this.modelHasCombination = !!this.model.combination;
    },
    toggleStandard(standard) {
      this.$set(
        this.model,
        "standards",
        _xor(this.model.standards || [], [standard])
      );
    },
    onSubmit() {
      this.$emit(
        "submit",
        _omit(
          {
            ...this.model,
            combination: this.modelHasCombination
              ? this.model.combination
              : null,
            combinationPart: this.modelHasCombination
              ? this.model.combinationPart
              : null,
            properties: _map(this.model.properties, (property) =>
              _omit(property, ["__typename"])
            ),
          },
          ["id", "__typename", "createdAt", "updatedAt", "variants"]
        )
      );
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/products/ProductForm.vue